import clsx from 'clsx';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router';

import type { RootState } from '$store';
import { contentClassesBase, sidebarClassesBase, wrapperClassesBase } from '~components/app/app-definitions';
import JiraRequestWidget from '~components/jira-request-widget/JiraRequestWidget';
import NavbarContainer from '~components/navigation/Navbar/NavbarContainer';
import NavbarHeader from '~components/navigation/topnavbar/NavbarHeader';
import Notifications from '~components/notifications/Notifications';
import RightSidebar from '~components/sidebar/RightSidebar';
import { Sidebar } from '~components/sidebar/Sidebar';
import { SidebarExpander } from '~components/sidebar-expander/SidebarExpander';
import { ProviderBag } from '~context/ProviderBag';
import SiteAccessSwitch from '~extension/siteaccess-switch';
import { registerSLOHandlers } from '~services/api/calls/user';
import { useAppLogin } from '~services/env';
import { getNavigationType } from '~store/getter';
import { selectContext, selectSidebarIsCollapsed } from '~store/selectors';
import { selectDocumentTitle, selectSessionName, selectUserIsLoggedIn } from '~store/selectors';
import { isLessThanSmall } from '~utils/browser';
import appStyles from '~components/app/styles.css';
import globalStyles from '~styles/GlobalStyles.module.css';

export const App = () => {
  const context = useSelector(state => selectContext(state as RootState));
  const isMenuEditing = context === 'main-navigation';

  const sessionName = useSelector(state => selectSessionName(state as RootState));
  const userIsLoggedIn = useSelector(state => selectUserIsLoggedIn(state as RootState));
  const isLoggedIn = useAppLogin ? Boolean(sessionName) : userIsLoggedIn;

  const collapsedSidebar = useSelector(state => selectSidebarIsCollapsed(state as RootState));

  // run initialize method of SLO again
  registerSLOHandlers();

  // Add top-navigation class when navigation type is top
  const navigationType = getNavigationType();
  if (navigationType === 'top') {
    document.body.classList.add('top-navigation');
  }

  if (!isLoggedIn) {
    document.body.classList.add('c-anonymous-page', 'in-app');
  }

  const documentTitle = useSelector(selectDocumentTitle);
  useEffect(() => {
    document.title = documentTitle;
  }, [documentTitle]);

  const wrapperClasses = clsx({
    ...wrapperClassesBase,
    [appStyles.wrapperSidebarCollapsed]: collapsedSidebar,
  });
  const sidebarClasses = clsx({
    ...sidebarClassesBase,
    'c-app__sidebar_expanded': isMenuEditing,
  });
  const contentClasses = clsx({
    ...contentClassesBase,
  });

  // TOOD: BUG! JiraRequestWidget is just awailable for left navigation!
  // BUG: users will not have a navigatop if top and isLessThanSmall!
  return (
    <ProviderBag>
      {isLoggedIn && navigationType === 'top' && !isLessThanSmall() && (
        <>
          <NavbarHeader />
          <NavbarContainer />
          <RightSidebar />
        </>
      )}
      <div className={wrapperClasses}>
        {isLoggedIn && (navigationType === null || navigationType === 'left') && (
          <>
            <JiraRequestWidget />

            <aside className={sidebarClasses}>
              <Sidebar />
            </aside>
            <SidebarExpander collapsed={collapsedSidebar} />
          </>
        )}
        <div className={contentClasses}>
          <main className={clsx(appStyles.main, 'c-main', globalStyles.globalTailwindClasses)}>
            <Outlet />
          </main>
        </div>
        <Notifications />
      </div>
      <SiteAccessSwitch />
    </ProviderBag>
  );
};

export default App;
